import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useI18n } from '../i18n/useI18n'
import { useAuth } from '../auth/useAuth'
import { useAsync } from '../lib/useAsync'
import { useSelectedYear } from '../year/useYear'
import { formatDate, formatMoney, formatPeriod } from '../lib/format'
import { canEnterData } from '../lib/types'
import { bankDepositBlock } from '../lib/rules'
import { Loading, ErrorState, EmptyState } from '../components/States'
import { DocumentLink } from '../components/DocumentViewer'
import { BankDepositSheet } from './BankDepositSheet'

export interface BankAccount {
  id: string
  name: string
  branch: string | null
  account_number: string | null
}

export interface BankDepositRow {
  id: string
  period: string
  account_id: string
  deposited_on: string
  amount_deposited: number
  amount_credited: number
  stamp_duty: number
  reference: string | null
  remitter: string | null
  slip_document: string
  account: { name: string } | null
}

/**
 * Versements en banque de l'annee.
 *
 * Les loyers sont encaisses puis verses en une seule fois par mois :
 * la piece justificative du mois est le BORDEREAU de ce versement. Ce
 * n'est donc pas un recu par locataire, et c'est ici qu'il se joint.
 *
 * Le bordereau est obligatoire : la colonne slip_document est NOT NULL
 * dans la base, et le bouton d'enregistrement reste donc inactif tant
 * qu'aucune photo n'est jointe. Mieux vaut un bouton grise qu'une
 * requete vouee a etre refusee.
 */
async function load(year: number, periods: string[]) {
  const [deposits, accounts] = await Promise.all([
    supabase
      .from('bank_deposits')
      .select(
        'id, period, account_id, deposited_on, amount_deposited,' +
          ' amount_credited, stamp_duty, reference, remitter, slip_document,' +
          ' account:bank_accounts(name)',
      )
      .in('period', periods)
      .order('deposited_on', { ascending: false }),
    supabase
      .from('bank_accounts')
      .select('id, name, branch, account_number')
      .eq('active', true)
      .order('name'),
  ])

  if (deposits.error) throw deposits.error
  if (accounts.error) throw accounts.error

  // Loyers encaisses du mois, pour l'ecart entre ce qui est rentre et
  // ce qui a ete credite en banque.
  const { data: rent, error: rErr } = await supabase
    .from('v_rent_status')
    .select('period, amount_received')
    .in('period', periods)
  if (rErr) throw rErr

  const encaisseParMois = new Map<string, number>()
  for (const r of (rent ?? []) as { period: string; amount_received: number }[]) {
    encaisseParMois.set(
      r.period,
      (encaisseParMois.get(r.period) ?? 0) + r.amount_received,
    )
  }

  // Mois clotures. Le declencheur trg_lock_bank_deposits refuse deja
  // toute ecriture et toute suppression sur ces mois : on le lit ici
  // pour l'annoncer a l'ecran plutot que de laisser partir une requete
  // vouee au refus.
  const { data: fermes, error: fErr } = await supabase
    .from('settlements')
    .select('period')
    .eq('status', 'ferme')
    .in('period', periods)
  if (fErr) throw fErr

  return {
    deposits: (deposits.data ?? []) as unknown as BankDepositRow[],
    accounts: (accounts.data ?? []) as BankAccount[],
    encaisseParMois,
    moisFermes: new Set((fermes ?? []).map((f) => (f as { period: string }).period)),
    year,
  }
}

export function BankScreen({ onBack }: { onBack: () => void }) {
  const { t, lang } = useI18n()
  const { profile } = useAuth()
  const { year, periods } = useSelectedYear()
  const { data, loading, error, reload } = useAsync(
    () => load(year, periods),
    [year],
  )
  const [sheet, setSheet] = useState<'closed' | 'new'>('closed')
  const [editing, setEditing] = useState<BankDepositRow | null>(null)

  const peutSaisir = canEnterData(profile?.role ?? null)
  const deposits = data?.deposits ?? []
  const moisFermes = data?.moisFermes ?? new Set<string>()

  const totalDepose = deposits.reduce((s, d) => s + d.amount_deposited, 0)
  const totalCredite = deposits.reduce((s, d) => s + d.amount_credited, 0)
  const totalTimbre = deposits.reduce((s, d) => s + d.stamp_duty, 0)

  // Regroupement par mois : un versement par mois en principe, donc un
  // groupe par mois se lit d'un coup d'oeil.
  const parMois = new Map<string, BankDepositRow[]>()
  for (const d of deposits) {
    const liste = parMois.get(d.period) ?? []
    liste.push(d)
    parMois.set(d.period, liste)
  }
  const mois = [...parMois.entries()].sort((a, b) => b[0].localeCompare(a[0]))

  return (
    <div className="screen">
      <button type="button" className="backlink" onClick={onBack}>
        ← {t('back')}
      </button>

      <div className="screen__head">
        <h1 className="screen__title">{t('bank_title')}</h1>
        {peutSaisir && (
          <button
            type="button"
            className="btn btn--primary btn--small"
            onClick={() => setSheet('new')}
          >
            {t('add')}
          </button>
        )}
      </div>

      <p className="muted">{t('bank_intro')}</p>

      {loading && <Loading />}
      {error && <ErrorState message={error} onRetry={reload} />}

      {!loading && !error && deposits.length === 0 && (
        <EmptyState title={t('bank_empty')} body={t('bank_empty_body')} />
      )}

      {deposits.length > 0 && (
        <>
          <div className="totals">
            <div>
              <span className="totals__label">{t('bank_total_deposited')}</span>
              <span className="totals__value">{formatMoney(totalDepose)}</span>
            </div>
            <div>
              <span className="totals__label">{t('bank_total_credited')}</span>
              <span className="totals__value totals__value--accent">
                {formatMoney(totalCredite)}
              </span>
            </div>
          </div>

          {/* Les frais de timbre restent une ligne a part : le cahier
              des charges interdit de les fondre avec les honoraires. */}
          {totalTimbre > 0 && (
            <p className="muted">
              {t('bank_total_stamp')} : {formatMoney(totalTimbre)}
            </p>
          )}

          {mois.map(([periode, liste]) => {
            const credite = liste.reduce((s, d) => s + d.amount_credited, 0)
            const encaisse = data?.encaisseParMois.get(periode) ?? 0
            const ecart = encaisse - credite

            return (
              <div key={periode} className="stack">
                <h2 className="screen__section">{formatPeriod(periode, lang)}</h2>

                {/* L'ecart du mois : ce qui est rentre, moins ce qui a
                    ete credite. C'est le chiffre que le cahier des
                    charges demande de voir. */}
                <div className={`banner ${ecart === 0 ? 'banner--warn' : 'banner--error'}`}>
                  <strong>
                    {t('bank_gap')} : {formatMoney(ecart)}
                  </strong>
                  <span>
                    {t('rent_total_received')} {formatMoney(encaisse)} −{' '}
                    {t('bank_total_credited')} {formatMoney(credite)}
                  </span>
                  {ecart !== 0 && <span>{t('bank_gap_hint')}</span>}
                </div>

                <ul className="list">
                  {liste.map((d) => (
                    <li key={d.id} className="list__item">
                      <span className="list__main">
                        {/* Nom de banque issu de la base, jamais traduit. */}
                        <span className="list__name">{d.account?.name ?? ''}</span>
                        <span className="list__meta">
                          {formatDate(d.deposited_on)}
                          {d.reference ? ` · ${d.reference}` : ''}
                        </span>
                        {d.remitter && (
                          <span className="list__note">
                            {t('bank_remitter')} : {d.remitter}
                          </span>
                        )}
                        {d.stamp_duty > 0 && (
                          <span className="list__note">
                            {t('bank_stamp_duty')} : {formatMoney(d.stamp_duty)}
                          </span>
                        )}
                        <span className="list__row">
                          <DocumentLink
                            documentId={d.slip_document}
                            label={t('bank_slip')}
                          />
                          {/* La ligne porte deja un bouton, celui du
                              bordereau : elle ne peut donc pas etre
                              elle-meme un bouton. La modification est
                              un bouton explicite, ce qui la rend aussi
                              plus visible qu'une ligne tapable. */}
                          {peutSaisir && (
                            <button
                              type="button"
                              className="btn btn--ghost btn--small"
                              onClick={() => setEditing(d)}
                            >
                              {t('edit')}
                            </button>
                          )}
                        </span>
                      </span>
                      <span className="list__side">
                        <span className="list__amount">
                          {formatMoney(d.amount_credited)}
                        </span>
                        {d.amount_credited !== d.amount_deposited && (
                          <span className="list__meta">
                            {t('bank_deposited')} {formatMoney(d.amount_deposited)}
                          </span>
                        )}
                        {/* Mois cloture : rien n'y bouge plus. */}
                        {bankDepositBlock(d.period, moisFermes) !== null && (
                          <span className="chip chip--warn">{t('period_closed')}</span>
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )
          })}
        </>
      )}

      {sheet === 'new' && (
        <BankDepositSheet
          year={year}
          accounts={data?.accounts ?? []}
          onClose={() => setSheet('closed')}
          onSaved={() => {
            setSheet('closed')
            reload()
          }}
        />
      )}

      {editing && (
        <BankDepositSheet
          year={year}
          accounts={data?.accounts ?? []}
          deposit={editing}
          locked={bankDepositBlock(editing.period, moisFermes) !== null}
          lockedReason={t('bank_locked_closed', {
            period: formatPeriod(editing.period, lang),
          })}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null)
            reload()
          }}
          onDeleted={() => {
            setEditing(null)
            reload()
          }}
        />
      )}
    </div>
  )
}
